/* globals App, ca */
/**
 * Gallery
 */
import { findIndex, has, isEqual, differenceWith } from 'lodash';
import pluralize from 'pluralize';
import { Media, FilterBuilder } from 'ca-api-client';

const defaultFilters = () => [
    { type: 'image,video,document,before_after,audio,other' },
    { interactive_id: null },
    { 360: null },
    { public: null },
    { set: 'root' },
];

// state
export const galleryState = {
    gallery: {
        limit: 10,
        sort: '-created_at',
        direction: 'ASC',
        page: 1,
        items: [],
        meta: {},
        selectedItems: [],
        selectedItemCount: 0,
        selectedItemsFilesize: 0,
        isLoading: false,
        isError: false,
        error: {
            title: null,
            msg: null,
        },
        currentFilter: defaultFilters(),
        currentFilterText: 'Filter by',
        currentFilterApplied: false,
        currentIndex: 0,
        sets: {
            isLoading: false,
            items: [],
        },
    },
};

// mutations
export const galleryMutations = {
    /**
     * Sets media gallery loading state
     * @param {boolean} loading
     */
    setGalleryIsLoading(state, loading) {
        state.gallery.isLoading = loading;
    },

    setMediaSetGalleryIsLoading(state, loading) {
        state.gallery.sets.isLoading = loading;
    },

    /**
     * Sets media gallery error state
     * @param {boolean} loading
     */
    setGalleryIsError(state, error) {
        state.gallery.isError = error;
    },

    /**
     * Sets media gallery error object
     * @param {object} error
     */
    setGalleryError(state, error) {
        state.gallery.error = error;
    },

    /**
     * Sets media gallery fetch limit
     * @param {number} limit
     */
    setGalleryLimit(state, limit) {
        state.gallery.limit = limit;
    },

    /**
     * Formats media gallery fetch sort field
     * @param {string} sort
     * Eg. created_at | -created_at
     */
    setGallerySort(state, sort) {
        if (state.gallery.direction === 'ASC') sort = `-${sort}`;
        state.gallery.sort = sort;
    },

    /**
     * Sets media gallery fetch direction
     * @param {string} direction - ASC|DESC
     */
    setGalleryDirection(state, direction) {
        state.gallery.direction = direction;
    },

    /**
     * Sets media gallery fetch page
     * @param {number} page
     */
    setGalleryPage(state, page) {
        state.gallery.page = page;
    },

    /**
     * Sets media gallery items
     * @param {array} items - Media
     */
    setGalleryMediaItems(state, items) {
        state.gallery.items = items;
    },

    setMediaSetItems(state, items) {
        state.gallery.sets.items = items;
    },

    /**
     * Sets media gallery fetch meta
     * @param {object} meta
     */
    setGalleryMediaItemsMeta(state, meta) {
        state.gallery.meta = meta;
    },

    /**
     * Sets media gallery item selected
     * @param {object} item
     */
    setGalleryMediaItemSelected(state, item) {
        const selected = state.gallery.selectedItems.map((i) => i.id);
        if (item.checked && !selected.includes(item.id)) {
            state.gallery.selectedItems.push(item);
        } else {
            const filteredArray = state.gallery.selectedItems.filter((e) => e.id !== item.id);
            state.gallery.selectedItems = filteredArray;
        }
        state.gallery.selectedItemCount = state.gallery.selectedItems.length;

        const totalSize = state.gallery.selectedItems
            .map((item) => item.size)
            .reduce((sum, current) => sum + current, 0);

        state.gallery.selectedItemsFilesize = totalSize;
    },

    /**
     * Sets media gallery item selected
     * @param {array} items - An array of media items
     */
    setGalleryMediaSelectedItems(state, items) {
        state.gallery.selectedItems = items;
        state.gallery.selectedItemCount = items.length;
    },

    /**
     * Sets media gallery item selected count
     * @param {number} count
     */
    setGalleryMediaItemSelectedCount(state, count) {
        state.gallery.selectedItemCount = count;
    },

    /**
     * Sets media gallery item selected filesize count
     * @param {number} size - bytes
     */
    setGalleryMediaItemSelectedFilesize(state, size) {
        state.gallery.selectedItemsFilesize = size;
    },

    /**
     * Sets media gallery item selected filesize count
     * @param {number} size - bytes
     */
    setGalleryFilter(state, payload) {
        const isChecked = payload.checked ? true : false;
        const currentFilter = state.gallery.currentFilter;
        const index = findIndex(currentFilter, payload.filter);
        const textFilterIndex = findIndex(currentFilter, 'text');

        if ((textFilterIndex === -1 && index === -1) || (index === -1 && isChecked)) {
            currentFilter.push(payload.filter);
        }

        if (textFilterIndex === -1 && index !== -1 && !isChecked) {
            currentFilter.splice(index, 1);
        }

        if (textFilterIndex !== -1 && index === -1 && !isChecked) {
            if (payload.filter.text) {
                currentFilter.splice(textFilterIndex, 1, payload.filter);
            } else {
                currentFilter.splice(textFilterIndex, 1);
            }
        }
    },

    /**
     * Sets media gallery item selected filesize count
     * @param {filter} array - Array of filter objects
     * Eg. [{type: 'image'}, {360: true}]
     */
    setGalleryCurrentFilter(state, filter) {
        if (!filter.length === 0) {
            filter = defaultFilters();
        }
        state.gallery.currentFilter = filter;
    },

    /**
     * Sets media gallery filters applied status
     * @returns {boolean}
     */
    setGalleryFilterApplied(state, applied) {
        return (state.gallery.currentFilterApplied = applied);
    },

    /**
     * Sets media gallery current item index
     * @param {number} index - the key of the items index
     */
    setGalleryCurrentIndex(state, index) {
        state.gallery.currentIndex = index;
    },

    /**
     * Appends a comment reply to an existing media item
     * @param {object} reply - An comment object
     * @see ca-api docs
     */
    appendGalleryCommentReply(state, reply) {
        const item = state.gallery.items.find((item) => item.id === reply.media_id);
        if (item && item.hasComments) {
            const comment = item.parent_comments.data.find((comment) => comment.id === reply.parent_id);
            if (comment && comment.hasOwnProperty('children')) {
                comment.children.data.push(reply);
            } else {
                comment.children = { data: [reply] };
            }
        }
    },

    /**
     * Increments / deincrements a media items comments_completed_count
     * @param {object} comment - An comment object
     * @see ca-api docs
     */
    updateGalleryComment(state, comment) {
        const item = state.gallery.items.find((item) => item.id === comment.media_id);
        switch (comment.status) {
            case 'complete':
                item.commentsCompletedCount = item.commentsCompletedCount + 1;
                break;
            case 'incomplete':
                item.commentsCompletedCount = item.commentsCompletedCount - 1;
                break;
        }
    },
};

// getters
export const galleryGetters = {
    /**
     * Gets media gallery loading state
     * @returns {boolean}
     */
    getGalleryIsLoading(state) {
        return state.gallery.isLoading;
    },

    getMediaSetGalleryIsLoading(state) {
        return state.gallery.sets.isLoading;
    },

    /**
     * Gets media gallery error state
     * @returns {boolean}
     */
    getGalleryIsError(state) {
        return state.gallery.isError;
    },

    /**
     * Gets media gallery error object
     * @returns {object}
     */
    getGalleryError(state) {
        return state.gallery.error;
    },

    /**
     * Gets media gallery fetch limit
     * @returns {number}
     */
    getGalleryLimit(state) {
        return state.gallery.limit;
    },

    /**
     * Gets media gallery fetch sort mode
     * Formatted sort mode eg. created_at | -created_at
     * @returns {string}
     */
    getGallerySort(state) {
        return state.gallery.sort;
    },

    /**
     * Gets media gallery current fetch direction
     * @returns {string} - ASC|DESC
     */
    getGalleryDirection(state) {
        return state.gallery.direction;
    },

    /**
     * Gets media gallery current fetch page
     * @returns {number}
     */
    getGalleryPage(state) {
        return state.gallery.page;
    },

    /**
     * Gets media gallery current fetch options
     * @returns {object}
     */
    getGalleryFetchOptions(state) {
        return {
            limit: state.gallery.limit,
            direction: state.gallery.direction,
            sort: state.gallery.sort,
        };
    },

    /**
     * Gets media gallery media items
     * @returns {array} - Media
     */
    getGalleryMediaItems(state) {
        return state.gallery.items;
    },

    getMediaSetItems(state) {
        return state.gallery.sets.items;
    },

    /**
     * Gets media gallery fetch metadata
     * @returns {object} - Media
     */
    getGalleryMediaItemsMeta(state) {
        return state.gallery.meta;
    },

    /**
     * Gets media gallery selected items
     * @returns {array} - Media
     */
    getGalleryMediaItemSelected(state) {
        return state.gallery.selectedItems;
    },

    /**
     * Gets media gallery selected item id's
     * @returns {array} - An array of media id's
     */
    getGalleryMediaItemSelectedIds(state) {
        return state.gallery.selectedItems.map((item) => item.id);
    },

    getGallerySelectedByType(state) {
        const selected = state.gallery.selectedItems;
        const media = selected.filter((item) => item.type !== 'gallery').map((item) => item.id);

        const selectedTypes = { media };

        const sets = selected.filter((item) => item.type === 'gallery').map((item) => item.id);

        selectedTypes.sets = sets;

        return selectedTypes;
    },

    /**
     * Gets media gallery selected item count
     * @returns {number}
     */
    getGalleryMediaItemSelectedCount(state) {
        return state.gallery.selectedItemCount;
    },

    /**
     * Gets media gallery selected item filesize total
     * @returns {number} - Bytes
     */
    getGalleryMediaItemSelectedFilesize(state) {
        return state.gallery.selectedItemsFilesize;
    },

    /**
     * Gets media gallery current filters
     * @returns {array} - An array of api filters
     */
    getGalleryCurrentFilter(state) {
        return state.gallery.currentFilter;
    },

    /**
     * Gets media gallery default filters
     * @returns {array} - An array of api filters
     */
    getGalleryDefaultFilter(state) {
        return defaultFilters();
    },

    /**
     * Gets media gallery filters dirty status
     * @returns {boolean}
     */
    getGalleryFilterDirty(state) {
        return !isEqual(state.gallery.currentFilter, defaultFilters());
    },

    /**
     * Gets media gallery filters applied statis
     * @returns {boolean}
     */
    getGalleryFilterApplied(state) {
        return state.gallery.currentFilterApplied;
    },

    /**
     * Gets media gallery filters count
     * @returns {number}
     */
    getGalleryFilterCount(state) {
        let count = 0;
        if (!isEqual(state.gallery.currentFilter, defaultFilters())) {
            const diff = differenceWith(state.gallery.currentFilter, defaultFilters(), isEqual);
            count = diff.length;
        }
        return count;
    },

    /**
     * Gets an gallery item by its id
     * @param {string} id
     * @returns {object} Media
     */
    getGalleryItem: (state) => (id) => {
        // Set current items position in gallery
        let index = Object.keys(state.gallery.items).find((key) => state.gallery.items[key].id === id);
        // return the current item
        return state.gallery.items[index];
    },

    /**
     * Gets an gallery media set item by its id
     * @param {string} id
     * @returns {object} Media
     */
    getGalleryMediaSetItem: (state) => (id) => {
        // Set current items position in gallery
        let index = Object.keys(state.gallery.sets.items).find((key) => state.gallery.sets.items[key].id === id);
        // return the current item
        return state.gallery.sets.items[index];
    },

    /**
     * Gets gallery items by an array of ids
     * @param {array} ids
     * @returns {array} Media(s)
     */
    getGalleryItemsById: (state) => (ids) => {
        return state.gallery.items.filter((item) => ids.includes(item.id));
    },

    /**
     * Find items by matching object properties
     * @param {object}
     * @returns {array} Media(s)
     */
    findByMatchingProperties: (state) => (properties) => {
        return state.gallery.items.filter((item) => {
            return Object.keys(properties).every((key) => {
                return item[key] === properties[key];
            });
        });
    },
};

// actions
export const galleryActions = {
    /**
     * Puts the gallery into error state
     * @param {object} payload - {error: true|false, title, message}
     * @return {object} Promise
     */
    setGalleryErrorState({ commit }, payload) {
        commit('setGalleryIsError', payload.error);
        commit('setGalleryError', payload);
        commit('setGalleryIsLoading', false);
    },

    /**
     * Sets the gallery media items & fetch metadata
     * @param {object} response - XHR api response
     * @return {object} Promise
     */
    setGalleryMedia({ commit, state }, response) {
        commit('setGalleryMediaItems', response.data);
        commit('setGalleryMediaItemsMeta', response.meta);
    },

    /**
     * Resets the gallery filters
     * @return {object} Promise
     */
    resetGalleryFilters({ commit }) {
        commit('setGalleryCurrentFilter', defaultFilters());
        commit('setGalleryFilterApplied', false);
    },

    /**
     * Removes a gallery filter from filter array
     * @param {object} filter
     * Eg. {type: 'image}
     * @return {object} Promise
     */
    removeGalleryFilter({ state }, filter = false) {
        const currentFilter = state.gallery.currentFilter;
        if (filter) {
            const index = findIndex(currentFilter, filter.value);

            if (index !== -1) {
                state.gallery.currentFilter.splice(index, 1);
            }
        }
    },

    /**
     * Removes a gallery media item from gallery
     * @param {string} id - Media items id
     * @return {object} Promise
     */
    removeGalleryMediaItem({ commit, state }, id) {
        return new Promise((resolve) => {
            if (!Array.isArray(id)) id = [id];

            if (state.gallery.items.length > 0) {
                const mediaItems = state.gallery.items.filter((item) => !id.includes(item.id));
                commit('setGalleryMediaItems', mediaItems);
            }

            if (state.gallery.sets.items.length > 0) {
                const mediaSets = state.gallery.sets.items.filter((item) => !id.includes(item.id));
                commit('setMediaSetItems', mediaSets);
            }

            resolve();
        });
    },

    /**
     * Removes items after delete from gallery
     * @param {array} itemIds - An array of Media items id's
     * @return {object} Promise
     */
    removeDeletedItems({ commit, dispatch }, itemIds) {
        const isMediaGallery = App.$route.name === 'gallery';
        const isMediaSet = App.$route.name === 'set';

        dispatch('removeGalleryMediaItem', itemIds).then(() => {
            let message = 'Item deleted.';
            if (Array.isArray(itemIds)) message = `${itemIds.length} ${pluralize('item', itemIds.length)} deleted.`;

            ca.notify({
                message,
                icon: 'trash',
                type: 'success',
                timeout: ca.config('toast.timeout'),
            });

            commit('setMediaDeleteModalItems', null); // reset delete items

            if (isMediaGallery) {
                commit('setGalleryMediaSelectedItems', []);
            } else if (isMediaSet) {
                dispatch('removeMediaSetItem', itemIds);
                commit('setMediaSetSelected', []);
            }

            if (!isMediaGallery && !isMediaSet) {
                App.$router.replace({ name: 'gallery' }).catch((err) => {});
            }
        });
    },

    /**
     * Removes any dependencies from existing media when a media item is deleted (http status: 409)
     * Currently only used for before_after media
     * @param {object} payload - {errorData: <XHR error response>}
     * @return {object} Promise
     */
    updateMediaDependencies({ getters, commit }, payload) {
        if (payload.errorData) {
            for (const prop in payload.errorData) {
                const current = payload.errorData[prop];
                if (prop === 'media' && Array.isArray(current)) {
                    current.forEach((item) => {
                        if (item.type === 'before_after') {
                            // remove media_id from before_after
                            const galleryItem = getters.getGalleryItem(item.id);
                            if (galleryItem) {
                                const beforeMediaId = galleryItem.metadata.before_after.before.media_id;
                                if (payload.id.includes(beforeMediaId))
                                    galleryItem.metadata.before_after.before.media_id = null;
                                const afterMediaId = galleryItem.metadata.before_after.after.media_id;
                                if (payload.id.includes(afterMediaId))
                                    galleryItem.metadata.before_after.after.media_id = null;
                            }
                        }
                    });
                }
            }
        }
    },

    /**
     * Appends an media item to the gallery
     * @param {object} item - Instance of Media
     * @return {object} Promise
     */
    appendGalleryMediaItem({ commit, state }, item) {
        state.gallery.items.push(item);
    },

    /**
     * Prepends an media item to the gallery
     * @param {object} item - Instance of Media
     * @return {object} Promise
     */
    prependGalleryMediaItem({ commit, state }, item) {
        state.gallery.items.unshift(item);
    },

    prependMediaSetItem({ commit, state }, item) {
        state.gallery.sets.items.unshift(item);
    },

    /**
     * Replaces an existing media item in the gallery if matches media.id
     * @param {object} media - Instance of Media
     * @return {object} Promise
     */
    replaceGalleryMediaItem({ commit, state }, media) {

        if (!(media instanceof Media)) {
            media = new Media(media);
        }

        const itemIndex = state.gallery.items.findIndex((item) => item.id === media.id);

        if (itemIndex > -1) {
            state.gallery.items.splice(itemIndex, 1, media);
        }
    },

    replaceGalleryMediaSetItem({ commit, state }, media) {
        const itemIndex = state.gallery.sets.items.findIndex((item) => item.id === media.id);

        if (itemIndex > -1) {
            state.gallery.sets.items.splice(itemIndex, 1, media);
        }
    },

    /**
     * Appends or replaces an existing media item in the gallery
     * @param {object} media - Instance of Media
     * @return {object} Promise
     */
    appendOrReplaceGalleryMediaItem({ commit, state }, media) {
        const itemIndex = state.gallery.items.findIndex((item) => item.id === media.id);
        if (itemIndex > -1) {
            state.gallery.items.splice(itemIndex, 1, media);
        } else {
            state.gallery.items.push(media);
        }

        console.log('MEDIA ITEM', state.gallery.items[itemIndex]);
    },

    /**
     * Prepends or replaces an existing media item in the gallery
     * @param {object} media - Instance of Media
     * @return {object} Promise
     */
    prependOrReplaceGalleryMediaItem({ commit, state }, media) {
        const itemIndex = state.gallery.items.findIndex((item) => item.id === media.id);
        if (itemIndex > -1) {
            state.gallery.items.splice(itemIndex, 1, media);
        } else {
            state.gallery.items.unshift(media);
        }
    },

    /**
     * Fetches gallery media items from api
     * @param {object} payload
     * @see https://github.com/robsontenorio/vue-api-query for available options
     * @see ca-api docs Media
     * @return {object} Promise
     */
    async fetchGalleryItems({ commit, dispatch, getters }, payload) {
        let isError = false;
        let params = {
            limit: payload.limit,
            sort: payload.sort,
            filter: { project_id: ca.project.uuid },
            include: 'parentCommentsCount,parentCommentsCompletedCount',
        };

        if (payload.page > 0) params.page = payload.page;

        if (payload.include) {
            const includes = params.include.split(',');
            if (!includes.includes(payload.include)) includes.push(payload.include);
            params.include = includes.join(',');
        }

        // add in payload filters if present
        if (payload.filters && Array.isArray(payload.filters) && payload.filters.length > 0) {
            const fb = new FilterBuilder();

            // filter out null filters before sending to api
            payload.filters.forEach((filter, index) => {
                Object.keys(filter).forEach((key) => {
                    if (payload.filters[index][key] !== null) fb.filter(filter);
                });
            });

            console.log('BUILD FILTERS', fb);

            params = Object.assign({}, params, fb.build());
        }

        console.log('PARAMS', params);

        // do media api query
        const media = await Media.params(params)
            .get()
            .catch((error) => {
                isError = true;
                dispatch('setGalleryErrorState', {
                    error: true,
                    title: "We're sorry, but something went wrong.",
                    msg: `Your request could not be completed. Please try again later, or check your system information <a class="alert-link" href="${route(
                        'systeminfo'
                    )}">here</a>.`,
                });
            });

        return media;
    },

    async fetchMediaSetItems({ dispatch }, params) {
        const sets = await dispatch('fetchMediaSets', {
            ...params,
            limit: -1,
            sort: 'position',
        });
        return sets;
    },

    /**
     * Fetches a single gallery media item from api
     * @param {string} id
     * @return {object} Promise
     */
    async fetchGalleryItem({ commit, getters }, id) {
        const params = {
            include: 'parentCommentsCount,parentCommentsCompletedCount',
        };
        const item = await Media.params(params).$find(id);
        return item;
    },

    /**
     * Deletes media item(s) using the api, triggers confirmation modal
     * @param {string|array} id
     * @return {object} Promise
     */
    async deleteGalleryItem({ commit, dispatch, getters }, id) {
        let media = null;

        if (Array.isArray(id)) {
            media = new Media();
        } else {
            media = await Media.$find(id);
        }

        return await media.delete(id).catch(async (error) => {
            if (error.response.status === 409) {
                let itemCount = 1;
                const errorData = error.response.data.data;
                if (Array.isArray(id)) itemCount = id.length;

                // titles cannot use line breaks as swal2 converts to <br>
                const title = `<span class="text-danger">By deleting ${pluralize('this', itemCount)} ${pluralize(
                    'item',
                    itemCount
                )} you will be removing it from the following media ${pluralize('item', errorData.count)}:</span>`;

                await ca.confirm(
                    {
                        title,
                        type: 'delete',
                        html: media.createDependencyListForDelete(id, errorData),
                        params: [errorData],
                    },
                    async (errorData) => {
                        return await media.delete(id, true).then((response) => {
                            dispatch('removeDeletedItems', id);
                            dispatch('updateMediaDependencies', { id, errorData });
                        });
                    }
                );
            }
        });
    },

    /**
     * Updates a gallery media item using the api
     * @param {object} payload - {id: <media_id>, data: <media item props>}
     * @return {object} Promise
     */
    async updateGalleryItem({ commit }, payload) {
        const media = await Media.$find(payload.id);
        if (media.description === null) delete media.description; // api fails to save as description must be a string
        // if (payload.data && Object.keys(payload.data).length > 0) {
        //     for (const prop in payload.data) {
        //         media[prop] = payload.data[prop];
        //         console.log(`updateGalleryItem - setting ${prop}: ${media[prop]} to ${payload.data[prop]}`);
        //     }
        // }
        return await media._update(payload.data);
    },

    /**
     * Shares a gallery media item using the api
     * @param {object} payload - { type, media, metadata }
     * @see ca-api docs Media share
     * @return {object} Promise
     */
    async shareGalleryItem({ commit }, payload) {
        const media = new Media();
        return await media.share(...payload);
    },

    /**
     * Updates many media items using the api
     * @param {object} payload - {id: <media_id>, data: <media item props>}
     * @return {object} Promise
     */
     async updateManyGalleryItem({ commit }, payload) {
        const media = new Media({});
        return await media.updateMany(payload);
    },

    /**
     * Clears galleries selectedItems
     * @return {object} Promise
     */
    clearGallerySelected({ commit }) {
        commit('setGalleryMediaSelectedItems', []);
    },
};
